import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn';

/**
 * The tab strip, once.
 *
 * It was hand-rolled in at least three screens — the employee detail page, the
 * HR setup page and company oversight — each with its own markup, its own
 * active style and none of the keyboard or ARIA behaviour. A detail page with
 * six sections is about to make that four.
 *
 * Selection lives in the URL when a `param` is given. That is not a detail: a
 * colleague sent the billing tab of a company should open on the billing tab,
 * and the browser's back button should leave a tab rather than the page.
 */

export interface TabSpec {
  key: string;
  label: string;
  /** A count or a badge beside the label. Omit rather than passing zero. */
  hint?: ReactNode;
  /** Hidden entirely when false — a tab nobody may open should not be shown. */
  visible?: boolean;
}

export function Tabs({ tabs, active, onChange, className }: {
  tabs: TabSpec[];
  active: string;
  onChange: (key: string) => void;
  className?: string;
}) {
  const shown = tabs.filter((t) => t.visible !== false);
  const strip = useRef<HTMLElement>(null);
  // On a phone the strip scrolls; a link to its fifth tab opened with the
  // chosen tab off-screen, so nothing said which section was showing.
  useEffect(() => {
    strip.current?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);

  // A strip wider than the screen says so: a fade and an arrow at the edge
  // that has more tabs. On a tablet there is no scrollbar to give it away.
  const [more, setMore] = useState({ left: false, right: false });
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const check = () => setMore((prev) => {
      const next = { left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 };
      return prev.left === next.left && prev.right === next.right ? prev : next;
    });
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    return () => { el.removeEventListener('scroll', check); ro?.disconnect(); };
  }, [shown.length]);
  const nudge = (dir: 1 | -1) => strip.current?.scrollBy({ left: dir * strip.current.clientWidth * 0.7, behavior: 'smooth' });

  return (
    <div className={cn('relative border-b border-neutral-200', className)}>
      {more.left && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={() => nudge(-1)}
          className="absolute inset-y-0 left-0 z-10 flex w-10 items-center justify-start bg-gradient-to-r from-neutral-50 via-neutral-50/90 to-transparent text-neutral-500"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {more.right && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={() => nudge(1)}
          className="absolute inset-y-0 right-0 z-10 flex w-10 items-center justify-end bg-gradient-to-l from-neutral-50 via-neutral-50/90 to-transparent text-neutral-500"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
      <nav
        ref={strip}
        role="tablist"
        aria-label="Sections"
        className="-mb-px flex gap-1 overflow-x-auto"
      >
        {shown.map((t) => {
          const selected = t.key === active;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={selected}
              // One stop in the tab order for the whole strip; the arrows
              // move within it (the ARIA tabs pattern).
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(t.key)}
              onKeyDown={(e) => {
                // Left and right move between tabs, which is what a screen
                // reader user is told a tablist does. Home and End jump.
                const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
                if (!keys.includes(e.key)) return;
                e.preventDefault();
                const i = shown.findIndex((x) => x.key === active);
                const next = e.key === 'Home' ? 0
                  : e.key === 'End' ? shown.length - 1
                    : e.key === 'ArrowRight' ? (i + 1) % shown.length
                      : (i - 1 + shown.length) % shown.length;
                onChange(shown[next]!.key);
                // Focus follows the selection. Without this the old tab kept
                // focus, so the next arrow counted from the wrong place and a
                // screen reader never announced the tab now showing.
                const strip = e.currentTarget.parentElement;
                requestAnimationFrame(() => {
                  strip?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
                });
              }}
              className={cn(
                'shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-small font-medium transition-colors',
                selected
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-800',
              )}
            >
              {t.label}
              {t.hint != null && (
                <span className={cn(
                  'ml-1.5 rounded-badge px-1.5 py-0.5 text-caption tabular-nums',
                  selected ? 'bg-primary-50 text-primary-700' : 'bg-neutral-100 text-neutral-600',
                )}
                >
                  {t.hint}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * The masthead every detail page shares: who this is, what state it is in,
 * and the actions that belong to it.
 *
 * Actions sit here rather than beside the section they affect, because a
 * reader who has scrolled to the third tab still has to be able to find
 * "suspend" without hunting for the tab it was hiding behind.
 */
export function DetailHeader({
  back, eyebrow, title, subtitle, badges, actions, leading, className,
}: {
  /** The list this record came from, as a link element. */
  back?: ReactNode;
  eyebrow?: ReactNode;
  title: string;
  subtitle?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  /** Something that stands for the record beside its name: a photo, an avatar. */
  leading?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('space-y-3', className)}>
      {back}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-[1_1_18rem] items-start gap-4">
          {leading && <div className="shrink-0">{leading}</div>}
          <div className="min-w-0 flex-1">
            {eyebrow && (
              <p className="text-caption font-medium uppercase tracking-wide text-neutral-500">
                {eyebrow}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-h2 text-neutral-900">{title}</h1>
              {badges}
            </div>
            {subtitle && <div className="mt-1 text-small text-neutral-500">{subtitle}</div>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/** The trail under a record: read when something needs explaining, not on the way to the day's work. */
const TRAIL = /^(history|activity)$/i;

/**
 * A labelled block on a detail page: a heading, an optional action, a body.
 *
 * On a phone a block can start folded behind its own heading — `phoneFolded`
 * — and open with one tap. A record's history and activity fold by default:
 * a case page was 2,600px on a phone, a third of it the trail. From `md` the
 * block is always open and the "Show" control is not drawn.
 */
export function DetailSection({ title, action, children, className, phoneFolded }: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Starts folded on a phone. Defaults to true for "History" and "Activity". */
  phoneFolded?: boolean;
}) {
  const foldable = phoneFolded ?? TRAIL.test(title);
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <section className={cn('rounded-card border border-neutral-200 bg-white p-4', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">
          {title}
        </h2>
        {action}
        {foldable && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((o) => !o)}
            className="-my-2.5 inline-flex h-10 shrink-0 items-center gap-1 text-caption font-medium text-primary-600 md:hidden"
          >
            {open ? 'Hide' : 'Show'}
            <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </div>
      <div id={bodyId} className={cn('mt-3', foldable && !open && 'max-md:hidden')}>{children}</div>
    </section>
  );
}
